import React, {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Field, Form, Formik, FormikProps} from 'formik';
import get from 'lodash/get';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import {WorkflowStatusItemTypes, WorkflowStatusFormTypes} from 'appRedux/actions/workflow/types';
import {RootReducer} from 'appRedux/reducers';

import FormikTextInput from 'components/AgentScreenComponents/_form/FormBuilderTextInput';
import AgentSaveButton from 'components/AgentScreenComponents/_buttons/AgentSaveButton';
import {validationSchemaCreate} from 'components/Forms/Workflow/validation';
import StatusColorRadioGroup from 'components/Forms/Workflow/workflowStatus/StatusColorRadioGroup';
import MdxEditorSmallField from 'components/MdxEditorField/MdxEditorSmallField';
import FormikSelectInput from 'components/AgentScreenComponents/_form/FormBuilderSelectInput';

import {STATUS_PREPARED, STATUS_REVIEW} from 'config/index';

interface WorkflowStatusFormInterface {
    initialValues: WorkflowStatusFormTypes;
    onSubmitClicked: (values: WorkflowStatusFormTypes) => void;
    currentLanguage: string;
}

const WorkflowStatusForm: FC<WorkflowStatusFormInterface> = ({initialValues, onSubmitClicked, currentLanguage}) => {
    const [t] = useTranslation();

    const [currentColor, setCurrentColor] = useState<string>(initialValues.color);
    const [currentColorRequester, setCurrentColorRequester] = useState<string>(initialValues.colorRequester);

    const {
        workflow: {workflowStatusesList},
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const statusesBeforeList: WorkflowStatusItemTypes[] = workflowStatusesList.filter(status =>
        [STATUS_REVIEW, STATUS_PREPARED].includes(String(status.statusType)),
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={() => validationSchemaCreate(t)}
            onSubmit={(values, {setErrors}) => {
                onSubmitClicked({
                    ...values,
                    setErrors,
                    color: currentColor,
                    colorRequester: currentColorRequester,
                });
            }}
        >
            {(formik: FormikProps<WorkflowStatusFormTypes>) => {
                return (
                    <Form>
                        <Field
                            as="select"
                            required
                            name={'statusBefore'}
                            label={t('orguser.workflows.workflowStatusParameters.statusBefore')}
                            options
                            component={FormikSelectInput}
                            fullwidth
                        >
                            {statusesBeforeList.map((item: WorkflowStatusItemTypes) => {
                                const {id, uuid, title, translations} = item;
                                const translationForOrgUser = get(translations, [currentLanguage, 'translation'], null);
                                return (
                                    <MenuItem key={`workflow-${uuid}`} value={id}>
                                        <Typography sx={{fontWeight: 600}}>
                                            {translationForOrgUser ? translationForOrgUser : title}
                                        </Typography>
                                    </MenuItem>
                                );
                            })}
                        </Field>
                        <Field
                            required
                            name="title"
                            placeholder={t('orguser.workflows.workflowStatusParameters.title')}
                            component={FormikTextInput}
                        />
                        <StatusColorRadioGroup
                            title={t('orguser.workflows.workflowStatusParameters.selectColor')}
                            parameter={'color'}
                            value={currentColor}
                            setValue={setCurrentColor}
                            isTouched={get(formik, ['touched', 'color'], false)}
                            errorMessage={get(formik, ['errors', 'color'], null)}
                        />
                        <Field
                            required
                            name="titleRequester"
                            placeholder={t('orguser.workflows.workflowStatusParameters.titleRequester')}
                            component={FormikTextInput}
                        />
                        <StatusColorRadioGroup
                            title={t('orguser.workflows.workflowStatusParameters.selectColorRequester')}
                            parameter={'colorRequester'}
                            value={currentColorRequester}
                            setValue={setCurrentColorRequester}
                            isTouched={get(formik, ['touched', 'colorRequester'], false)}
                            errorMessage={get(formik, ['errors', 'colorRequester'], null)}
                        />
                        <MdxEditorSmallField
                            name="description"
                            value={formik.values.description ? String(formik.values.description) : ''}
                            label={t('orguser.workflows.workflowStatusParameters.description')}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mt: 3,
                            }}
                        >
                            <AgentSaveButton isSubmit />
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WorkflowStatusForm;
